/**
 * DialPad — phone keypad component.
 *
 * Two modes:
 *  - Normal mode: a formatted, keyboard-editable phone input (shared PhoneInput) plus a keypad and a
 *    Call button. Submitting the form (Enter, or the Call button) places the call via
 *    useVoiceCall().makeCall(). Used by DialerModal (desktop web softphone).
 *  - DTMF mode (dtmfMode=true): digits are sent as in-call DTMF tones; no input or Call button.
 *    Rendered inside ActiveCallOverlay (mobile) and FloatingCallWidget (desktop).
 */

'use client';

import { useState } from 'react';
import { Delete, Phone } from 'lucide-react';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { stripPhoneToDigits } from '@/lib/phoneUtils';

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
] as const;

/**
 * Best-effort E.164 for the dialed number. The input holds bare digits, but Telnyx expects a dialable
 * number and every stored contact number in this app is E.164 — so assume US/NANP for 10 digits and a
 * country code for anything longer.
 */
function toDialable(digits: string): string {
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

interface DialPadProps {
  /** When true, taps send DTMF digits to the active call instead of building a number. */
  dtmfMode?: boolean;
  /** Pre-fill the number input (normal mode only). */
  initialNumber?: string;
  /** Caller-ID number to use when placing a call (normal mode). */
  callerIdE164?: string;
  /** Display name for the callee (normal mode). */
  displayName?: string;
  /** Called with each digit pressed in DTMF mode (for parent tracking). */
  onDigitPressed?: (digit: string) => void;
}

export function DialPad({
  dtmfMode = false,
  initialNumber = '',
  callerIdE164,
  displayName,
  onDigitPressed,
}: DialPadProps) {
  const { makeCall, callState, sendDtmf } = useVoiceCall();
  const [number, setNumber] = useState(() => stripPhoneToDigits(initialNumber));

  function handleKey(key: string) {
    if (dtmfMode) {
      sendDtmf(key);
      onDigitPressed?.(key);
      return;
    }
    setNumber((n) => n + key);
  }

  function handleBackspace() {
    setNumber((n) => n.slice(0, -1));
  }

  const canCall = !!number && callState === 'idle';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canCall) return;
    await makeCall(toDialable(number), callerIdE164 ?? '', displayName);
  }

  // DTMF mode: just the key grid (kept compact — it renders inside the call overlay/widget).
  if (dtmfMode) {
    return (
      <div className="flex flex-col items-center gap-4">
        <div className="grid grid-cols-3 gap-3">
          {KEYS.flat().map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => handleKey(key)}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-xl font-semibold text-white transition-transform hover:bg-white/20 active:scale-95"
              aria-label={`Dial ${key}`}
            >
              {key}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center gap-5">
      {/* Number input — formatted + keyboard-editable; Enter submits the form to place the call. */}
      <div className="flex w-full items-center gap-2 rounded-xl bg-white/10 px-3 py-2.5 focus-within:ring-2 focus-within:ring-blue-400/60">
        <PhoneInput
          value={number}
          onChange={setNumber}
          placeholder="Enter number"
          className="min-w-0 flex-1 bg-transparent text-center text-2xl font-mono tracking-wider text-white placeholder:text-base placeholder:font-sans placeholder:tracking-normal placeholder:text-white/30 focus:outline-none"
        />
        {number.length > 0 && (
          <button
            type="button"
            onClick={handleBackspace}
            aria-label="Backspace"
            className="flex-shrink-0 text-white/60 transition-colors hover:text-white"
          >
            <Delete className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Key grid */}
      <div className="grid grid-cols-3 gap-3">
        {KEYS.flat().map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => handleKey(key)}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-2xl font-semibold text-white transition-transform hover:bg-white/20 active:scale-95"
            aria-label={`Dial ${key}`}
          >
            {key}
          </button>
        ))}
      </div>

      {/* Call button */}
      <button
        type="submit"
        disabled={!canCall}
        className="mt-1 flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-lg transition-transform hover:bg-green-600 active:scale-95 disabled:opacity-40 disabled:hover:bg-green-500"
        aria-label="Call"
      >
        <Phone className="h-7 w-7 text-white" />
      </button>
    </form>
  );
}
